import React, { useState, useEffect } from "react";
import { base44 } from "@voult/api/base44Client";
import { ShieldAlert, ShieldCheck, AlertTriangle, AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function SecurityDashboard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    base44.entities.SecurityEvent.list("-created_date", 100).then(setEvents).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const critical = events.filter((e) => e.severity === "critical");
  const warnings = events.filter((e) => e.severity === "warning");
  const unresolved = events.filter((e) => !e.resolved);
  const recent = events.slice(0, 10);

  const overallStatus = critical.length > 0 ? "critical" : warnings.length > 0 ? "warning" : "secure";

  const STATUS_CONFIG = {
    secure: { label: "Secure", cls: "bg-emerald-100 text-emerald-800", icon: ShieldCheck, ring: "ring-emerald-200" },
    warning: { label: "Attention Needed", cls: "bg-yellow-100 text-yellow-800", icon: AlertTriangle, ring: "ring-yellow-200" },
    critical: { label: "Critical Issues Detected", cls: "bg-rose-100 text-rose-800", icon: AlertCircle, ring: "ring-rose-200" },
  };

  const cfg = STATUS_CONFIG[overallStatus];
  const StatusIcon = cfg.icon;

  const stats = [
    { label: "Total Events", value: events.length, color: "text-slate-900" },
    { label: "Critical", value: critical.length, color: "text-rose-600" },
    { label: "Warnings", value: warnings.length, color: "text-yellow-600" },
    { label: "Unresolved", value: unresolved.length, color: "text-indigo-600" },
  ];

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Security Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Real-time security status and access monitoring</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className={`flex items-center gap-4 rounded-2xl p-5 mb-6 ring-2 ${cfg.ring} ${cfg.cls}`}>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/60">
          <StatusIcon className="h-6 w-6" />
        </div>
        <div>
          <p className="font-semibold text-lg">{cfg.label}</p>
          <p className="text-sm opacity-75">Last checked just now · {events.length} events total</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
            <p className={`text-3xl font-semibold ${color}`}>{value}</p>
            <p className="text-xs text-slate-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">Recent Events</h2>
        </div>
        {recent.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-emerald-400" />
            <p>No security events recorded yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {recent.map((event) => (
              <div key={event.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className={`h-2 w-2 rounded-full flex-shrink-0 ${event.severity === "critical" ? "bg-rose-500" : event.severity === "warning" ? "bg-yellow-400" : "bg-emerald-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800">{event.description || event.event_type?.replace(/_/g, " ")}</p>
                  {event.ip_address && <p className="text-xs text-slate-400">IP: {event.ip_address}</p>}
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${event.severity === "critical" ? "bg-rose-100 text-rose-700" : event.severity === "warning" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}`}>{event.severity}</span>
                  <p className="text-xs text-slate-400 mt-0.5">{event.created_date ? formatDistanceToNow(new Date(event.created_date), { addSuffix: true }) : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}